/**
 * The native date picker, wearing the shared field's border and audit hook.
 *
 * Two call sites, both in DataViz — a chart's X column when its axis is dates, and the sonification
 * threshold that marks a point on that axis. Small enough to be tempting to leave as a raw
 * `<input type="date">`, which is exactly why it is here: an exemption is a place the next person
 * adds a second exemption, and "every entry field in the task builder is a shared component" is
 * worth more than the twenty lines it costs.
 *
 * Genuinely NOT a `TextField` variant. The value is a `yyyy-mm-dd` string the browser both parses
 * and renders in the viewer's own locale, and the control it draws is a calendar rather than a
 * caret — there is no grammar to apply and no text to sanitise. Making it a variant would put a
 * branch inside TextField that skipped everything TextField does.
 */

export interface DateFieldProps {
  /** A native date-input value: `yyyy-mm-dd`, or empty. Never a locale-formatted date. */
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
  fieldId?: string;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}

export function DateField({ value, onChange, hasError, fieldId, ariaLabel, disabled, className = '' }: DateFieldProps) {
  // No `useDelayedStickyError` here, unlike the text and number fields. Their rule exists because a
  // half-typed value is transiently invalid; a date arrives whole from the picker, so there is no
  // in-between state to wait out and a delay would only postpone a fault that is already final.
  const border = hasError ? 'border-ws-error-fg' : 'border-ws-border';
  return (
    <input
      type='date'
      data-field-kind='date'
      data-field-id={fieldId}
      aria-label={ariaLabel}
      aria-invalid={hasError || undefined}
      disabled={disabled}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`min-w-0 rounded-md border ${border} bg-ws-btn px-3 py-1.5 text-sm text-ws-text outline-none disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
    />
  );
}
